import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Form, Row, Col, Alert } from 'react-bootstrap';
import { Modal } from '../patterns';
import {
  sprayService,
  SprayMachine,
  SprayCalibration,
  SprayCalibrationInput,
  IntensityLabel,
} from '../../services/sprayService';
import { deriveValues as deriveCalibrationValues } from '../../utils/calibrationDerivation';
import CalibrationHistoryModal from './CalibrationHistoryModal';

interface Props {
  machineId: string;
  /**
   * When supplied, the modal renders in Edit mode: form pre-fills from the
   * calibration's values, the title changes to "Edit Calibration", and Save
   * calls editCalibration (PUT) instead of addCalibration (POST). Track
   * machine_calibration_overrides_20260427 / Phase 3.
   */
  calibration?: SprayCalibration;
  onClose: () => void;
  onSaved: (machine: SprayMachine) => void;
}

interface IntensityRow {
  label: IntensityLabel;
  gear: string;
  pressure: string;
  deliveryPerMinute: string;
  metersPerMinute: string;
  // Operator-typed override values. Empty string = no override (ghost-text
  // derived value is shown). Track: machine_calibration_overrides_20260427.
  volumePerTreeOverride: string;
  litresPerHectareOverride: string;
}

const MAX_INTENSITIES = 4;

const INTENSITY_LABELS: IntensityLabel[] = ['low', 'medium', 'medium/heavy', 'heavy'];

const parseNumberOrUndefined = (s: string): number | undefined => {
  const trimmed = s.trim();
  if (trimmed === '') return undefined;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : undefined;
};

// Display formatter for calculated/derived values — always 2 decimal places.
// Raw precision is preserved in state and in the payload.
const formatDerived = (n?: number): string =>
  n === undefined || n === null ? '' : n.toFixed(2);

const emptyRow = (label: IntensityLabel = 'low'): IntensityRow => ({
  label,
  gear: '',
  pressure: '',
  deliveryPerMinute: '',
  metersPerMinute: '',
  volumePerTreeOverride: '',
  litresPerHectareOverride: '',
});

const hasOverride = (row: IntensityRow) =>
  row.volumePerTreeOverride.trim() !== '' || row.litresPerHectareOverride.trim() !== '';

// Save-button gating rule (refinement, 2026-04-27):
// Each intensity row must yield at least one of volume/tree or L/ha — either
// because the operator typed an override, or because the inputs (delivery,
// speed, tree spacing, trees/ha) are sufficient to derive it. A row with
// neither is dead weight and the operator should remove it instead.
const isRowPopulated = (
  row: IntensityRow,
  treeSpacing?: number,
  treesPerHectare?: number
): boolean => {
  const derived = deriveCalibrationValues({
    deliveryPerMinute: parseNumberOrUndefined(row.deliveryPerMinute),
    metersPerMinute: parseNumberOrUndefined(row.metersPerMinute),
    treeSpacing,
    treesPerHectare,
  });
  const volumeOverride = parseNumberOrUndefined(row.volumePerTreeOverride);
  const litresOverride = parseNumberOrUndefined(row.litresPerHectareOverride);
  const volumeEffective = volumeOverride ?? derived.volumePerTree;
  const litresEffective = litresOverride ?? derived.litresPerHectare;
  return volumeEffective !== undefined || litresEffective !== undefined;
};

const deriveValues = (
  row: IntensityRow,
  treeSpacing?: number,
  treesPerHectare?: number
) =>
  deriveCalibrationValues({
    deliveryPerMinute: parseNumberOrUndefined(row.deliveryPerMinute),
    metersPerMinute: parseNumberOrUndefined(row.metersPerMinute),
    treeSpacing,
    treesPerHectare,
  });

// Convert a saved calibration into the modal's internal row shape.
const calibrationToRows = (cal: SprayCalibration): IntensityRow[] => {
  if (!cal.intensityLevels || cal.intensityLevels.length === 0) return [emptyRow()];
  return cal.intensityLevels.map(lvl => ({
    label: lvl.label,
    gear: lvl.gear ?? '',
    pressure: lvl.pressure !== undefined ? String(lvl.pressure) : '',
    deliveryPerMinute: lvl.deliveryPerMinute !== undefined ? String(lvl.deliveryPerMinute) : '',
    metersPerMinute: lvl.metersPerMinute !== undefined ? String(lvl.metersPerMinute) : '',
    volumePerTreeOverride: lvl.volumePerTreeOverride !== undefined ? String(lvl.volumePerTreeOverride) : '',
    litresPerHectareOverride: lvl.litresPerHectareOverride !== undefined ? String(lvl.litresPerHectareOverride) : '',
  }));
};

// Normalise an HTML date input value: <input type="date"> needs YYYY-MM-DD,
// while saved calibrations may carry full ISO strings or Date-stringified.
const toDateInputValue = (raw?: string): string => {
  if (!raw) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toISOString().slice(0, 10);
};

const CalibrationModal: React.FC<Props> = ({ machineId, calibration, onClose, onSaved }) => {
  const { t } = useTranslation();
  // Edit mode is gated on the source row having an `_id`. A clone is a
  // calibration with the `_id` stripped (see utils/cloneCalibration), so
  // the modal pre-fills its values but Save still calls addCalibration.
  const isEditMode = !!calibration?._id;

  const [date, setDate] = useState(() => toDateInputValue(calibration?.date));
  const [performedBy, setPerformedBy] = useState(calibration?.performedBy ?? '');
  const [signedOffBy, setSignedOffBy] = useState(calibration?.signedOffBy ?? '');
  const [treeSpacing, setTreeSpacing] = useState(
    calibration?.treeSpacing !== undefined ? String(calibration.treeSpacing) : ''
  );
  const [treesPerHectare, setTreesPerHectare] = useState(
    calibration?.treesPerHectare !== undefined ? String(calibration.treesPerHectare) : ''
  );
  const [rows, setRows] = useState<IntensityRow[]>(
    calibration ? calibrationToRows(calibration) : [emptyRow()]
  );
  const [errors, setErrors] = useState<{ date?: string; performedBy?: string; override?: string; attachment?: string }>({});
  const [apiError, setApiError] = useState('');
  const [saving, setSaving] = useState(false);
  // Optional signed sign-off PDF — uploaded after the calibration is created.
  // Track: machine_calibration_overrides_20260427 / Phase 2.
  const [attachment, setAttachment] = useState<File | null>(null);
  // Audit-history modal toggle (Edit mode). Track Phase 3.
  const [showHistory, setShowHistory] = useState(false);
  const historyHasEntries = !!calibration?.editHistory && calibration.editHistory.length > 0;

  const treeSpacingNum = parseNumberOrUndefined(treeSpacing);
  const treesPerHectareNum = parseNumberOrUndefined(treesPerHectare);

  const allRowsPopulated = rows.every(row =>
    isRowPopulated(row, treeSpacingNum, treesPerHectareNum)
  );

  const updateRow = (index: number, patch: Partial<IntensityRow>) => {
    setRows(prev => {
      const updated = prev.map((r, i) => (i === index ? { ...r, ...patch } : r));
      if ('label' in patch) {
        updated.sort((a, b) => INTENSITY_LABELS.indexOf(a.label) - INTENSITY_LABELS.indexOf(b.label));
      }
      return updated;
    });
  };

  const addRow = () => {
    if (rows.length >= MAX_INTENSITIES) return;
    const usedLabels = new Set(rows.map(r => r.label));
    const nextLabel = INTENSITY_LABELS.find(l => !usedLabels.has(l)) ?? INTENSITY_LABELS[0];
    setRows(prev =>
      [...prev, emptyRow(nextLabel)].sort(
        (a, b) => INTENSITY_LABELS.indexOf(a.label) - INTENSITY_LABELS.indexOf(b.label)
      )
    );
  };

  const removeRow = (index: number) => {
    if (rows.length <= 1) return;
    setRows(prev => prev.filter((_, i) => i !== index));
  };

  const recalculateRow = (index: number) => {
    updateRow(index, { volumePerTreeOverride: '', litresPerHectareOverride: '' });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const nextErrors: { date?: string; performedBy?: string; override?: string } = {};
    if (!date.trim()) nextErrors.date = t('spray.machines.calibrations.dateRequired');
    if (!performedBy.trim()) nextErrors.performedBy = t('spray.machines.calibrations.performedByRequired');

    // Override values must be strictly positive. Empty is fine (no override).
    const invalidOverride = rows.some(row => {
      for (const raw of [row.volumePerTreeOverride, row.litresPerHectareOverride]) {
        const trimmed = raw.trim();
        if (trimmed === '') continue;
        const n = Number(trimmed);
        if (!Number.isFinite(n) || n <= 0) return true;
      }
      return false;
    });
    if (invalidOverride) {
      nextErrors.override = t('spray.machines.calibrations.overridePositive');
    }

    setErrors(nextErrors);
    setApiError('');
    if (nextErrors.date || nextErrors.performedBy || nextErrors.override) return;

    const payload: SprayCalibrationInput = {
      date: date.trim(),
      performedBy: performedBy.trim(),
      signedOffBy: signedOffBy.trim() || undefined,
      treeSpacing: treeSpacingNum,
      treesPerHectare: treesPerHectareNum,
      intensityLevels: rows.map(row => {
        const derived = deriveValues(row, treeSpacingNum, treesPerHectareNum);
        const volumeOverride = parseNumberOrUndefined(row.volumePerTreeOverride);
        const litresOverride = parseNumberOrUndefined(row.litresPerHectareOverride);
        return {
          label: row.label,
          gear: row.gear.trim() || undefined,
          pressure: parseNumberOrUndefined(row.pressure),
          deliveryPerMinute: parseNumberOrUndefined(row.deliveryPerMinute),
          metersPerMinute: parseNumberOrUndefined(row.metersPerMinute),
          treesPerMinute: derived.treesPerMinute,
          volumePerTreeDerived: derived.volumePerTree,
          volumePerTreeOverride: volumeOverride,
          volumePerTree: volumeOverride ?? derived.volumePerTree,
          litresPerHectareDerived: derived.litresPerHectare,
          litresPerHectareOverride: litresOverride,
          litresPerHectare: litresOverride ?? derived.litresPerHectare,
        };
      }),
    };

    setSaving(true);
    try {
      let updated: SprayMachine;
      let targetCalibrationId: string | undefined;
      if (isEditMode && calibration?._id) {
        updated = await sprayService.editCalibration(machineId, calibration._id, payload);
        targetCalibrationId = calibration._id;
      } else {
        updated = await sprayService.addCalibration(machineId, payload);
        targetCalibrationId = updated.calibrations?.[updated.calibrations.length - 1]?._id;
      }
      // If a signed PDF was picked, upload it. In Add mode, the calibration
      // was just created so we use the last calibration's _id; in Edit mode
      // we use the existing one. Upload failure is non-blocking — the
      // value save already succeeded.
      if (attachment && targetCalibrationId) {
        try {
          updated = await sprayService.uploadCalibrationAttachment(
            machineId,
            targetCalibrationId,
            attachment
          );
        } catch (uploadErr: any) {
          setApiError(t('spray.machines.calibrations.attachUploadError'));
        }
      }
      onSaved(updated);
    } catch (err: any) {
      const serverError = err?.response?.data?.error;
      setApiError(serverError ?? err?.message ?? t('spray.machines.calibrations.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleAttachmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (!file) {
      setAttachment(null);
      setErrors(prev => ({ ...prev, attachment: undefined }));
      return;
    }
    if (file.type !== 'application/pdf') {
      setAttachment(null);
      setErrors(prev => ({ ...prev, attachment: t('spray.machines.calibrations.attachInvalidType') }));
      return;
    }
    setAttachment(file);
    setErrors(prev => ({ ...prev, attachment: undefined }));
  };

  return (
    <Modal
      show
      onHide={onClose}
      size="lg"
      title={
        isEditMode
          ? t('spray.machines.calibrations.editCalibration')
          : t('spray.machines.calibrations.addCalibration')
      }
    >
      <Form onSubmit={handleSave}>
        <Modal.Body>
          {apiError && <Alert variant="danger" role="alert">{apiError}</Alert>}

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label htmlFor="cal-date">
                  {t('spray.machines.calibrations.date')}
                </Form.Label>
                <Form.Control
                  id="cal-date"
                  type="date"
                  value={date}
                  onChange={e => setDate(e.target.value)}
                  isInvalid={!!errors.date}
                />
                <Form.Control.Feedback type="invalid">{errors.date}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label htmlFor="cal-performed-by">
                  {t('spray.machines.calibrations.performedBy')}
                </Form.Label>
                <Form.Control
                  id="cal-performed-by"
                  type="text"
                  value={performedBy}
                  onChange={e => setPerformedBy(e.target.value)}
                  isInvalid={!!errors.performedBy}
                />
                <Form.Control.Feedback type="invalid">{errors.performedBy}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label htmlFor="cal-signed-off-by">
                  {t('spray.machines.calibrations.signedOffBy')}
                </Form.Label>
                <Form.Control
                  id="cal-signed-off-by"
                  type="text"
                  value={signedOffBy}
                  onChange={e => setSignedOffBy(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col md={6}>
              <Form.Group>
                <Form.Label htmlFor="cal-tree-spacing">
                  {t('spray.machines.calibrations.treeSpacing')}
                </Form.Label>
                <Form.Control
                  id="cal-tree-spacing"
                  type="number"
                  step="any"
                  min={0}
                  value={treeSpacing}
                  onChange={e => setTreeSpacing(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label htmlFor="cal-trees-per-hectare">
                  {t('spray.machines.calibrations.treesPerHectare')}
                </Form.Label>
                <Form.Control
                  id="cal-trees-per-hectare"
                  type="number"
                  step="any"
                  min={0}
                  value={treesPerHectare}
                  onChange={e => setTreesPerHectare(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>

          <div className="d-flex justify-content-between align-items-center mb-2">
            <h4 className="h6 mb-0">{t('spray.machines.calibrations.intensityLevels')}</h4>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={addRow}
              disabled={rows.length >= MAX_INTENSITIES}
              type="button"
            >
              {t('spray.machines.calibrations.addIntensity')}
            </Button>
          </div>

          {rows.map((row, index) => {
            const derived = deriveValues(row, treeSpacingNum, treesPerHectareNum);
            const labelIdPrefix = `cal-intensity-${index}`;
            return (
              <fieldset
                key={index}
                aria-label={`${t('spray.machines.calibrations.intensityLevels')} ${index + 1}`}
                role="group"
                className="border rounded p-2 mb-2"
              >
                <Row className="g-2 mb-2">
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label htmlFor={`${labelIdPrefix}-label`}>
                        {t('spray.machines.calibrations.intensityLabel')}
                      </Form.Label>
                      <Form.Select
                        id={`${labelIdPrefix}-label`}
                        value={row.label}
                        onChange={e => updateRow(index, { label: e.target.value as IntensityLabel })}
                      >
                        {INTENSITY_LABELS.filter(l =>
                          l === row.label || !rows.some((r, ri) => ri !== index && r.label === l)
                        ).map(l => (
                          <option key={l} value={l}>
                            {l}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label htmlFor={`${labelIdPrefix}-gear`}>
                        {t('spray.machines.calibrations.gear')}
                      </Form.Label>
                      <Form.Control
                        id={`${labelIdPrefix}-gear`}
                        type="text"
                        value={row.gear}
                        onChange={e => updateRow(index, { gear: e.target.value })}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label htmlFor={`${labelIdPrefix}-pressure`}>
                        {t('spray.machines.calibrations.pressure')}
                      </Form.Label>
                      <Form.Control
                        id={`${labelIdPrefix}-pressure`}
                        type="number"
                        step="any"
                        min={0}
                        value={row.pressure}
                        onChange={e => updateRow(index, { pressure: e.target.value })}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label htmlFor={`${labelIdPrefix}-delivery`}>
                        {t('spray.machines.calibrations.deliveryPerMinute')}
                      </Form.Label>
                      <Form.Control
                        id={`${labelIdPrefix}-delivery`}
                        type="number"
                        step="any"
                        min={0}
                        value={row.deliveryPerMinute}
                        onChange={e => updateRow(index, { deliveryPerMinute: e.target.value })}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label htmlFor={`${labelIdPrefix}-speed`}>
                        {t('spray.machines.calibrations.metersPerMinute')}
                      </Form.Label>
                      <Form.Control
                        id={`${labelIdPrefix}-speed`}
                        type="number"
                        step="any"
                        min={0}
                        value={row.metersPerMinute}
                        onChange={e => updateRow(index, { metersPerMinute: e.target.value })}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={1} className="d-flex align-items-end">
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => removeRow(index)}
                      disabled={rows.length <= 1}
                      type="button"
                    >
                      {t('spray.machines.calibrations.removeIntensity')}
                    </Button>
                  </Col>
                </Row>
                <Row className="g-2 align-items-end">
                  <Col md={{ span: 3, offset: 3 }}>
                    <Form.Group>
                      <Form.Label htmlFor={`${labelIdPrefix}-trees-per-min`}>
                        {t('spray.machines.calibrations.treesPerMinute')}
                      </Form.Label>
                      <Form.Control
                        id={`${labelIdPrefix}-trees-per-min`}
                        type="text"
                        readOnly
                        value={formatDerived(derived.treesPerMinute)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label htmlFor={`${labelIdPrefix}-volume-per-tree`}>
                        {t('spray.machines.calibrations.volumePerTree')}
                        {row.volumePerTreeOverride.trim() !== '' && (
                          <> ({t('spray.machines.calibrations.manual')})</>
                        )}
                      </Form.Label>
                      <Form.Control
                        id={`${labelIdPrefix}-volume-per-tree`}
                        type="number"
                        step="any"
                        min={0}
                        value={row.volumePerTreeOverride}
                        placeholder={formatDerived(derived.volumePerTree)}
                        onChange={e => updateRow(index, { volumePerTreeOverride: e.target.value })}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label htmlFor={`${labelIdPrefix}-litres-per-hectare`}>
                        {t('spray.machines.calibrations.litresPerHectare')}
                        {row.litresPerHectareOverride.trim() !== '' && (
                          <> ({t('spray.machines.calibrations.manual')})</>
                        )}
                      </Form.Label>
                      <Form.Control
                        id={`${labelIdPrefix}-litres-per-hectare`}
                        type="number"
                        step="any"
                        min={0}
                        value={row.litresPerHectareOverride}
                        placeholder={formatDerived(derived.litresPerHectare)}
                        onChange={e => updateRow(index, { litresPerHectareOverride: e.target.value })}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                {hasOverride(row) && (
                  <div className="d-flex justify-content-end mt-2">
                    <Button
                      variant="link"
                      size="sm"
                      type="button"
                      onClick={() => recalculateRow(index)}
                    >
                      ↻ {t('spray.machines.calibrations.recalculate')}
                    </Button>
                  </div>
                )}
              </fieldset>
            );
          })}
          {errors.override && (
            <Alert variant="danger" role="alert" className="mt-2">
              {errors.override}
            </Alert>
          )}

          <Row className="mt-4">
            <Col md={12}>
              <Form.Group>
                <Form.Label htmlFor="cal-attachment">
                  {t('spray.machines.calibrations.attachSignedDoc')}
                </Form.Label>
                <Form.Control
                  id="cal-attachment"
                  type="file"
                  accept="application/pdf"
                  onChange={handleAttachmentChange}
                  isInvalid={!!errors.attachment}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.attachment}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex flex-column align-items-stretch w-100">
            {!allRowsPopulated && (
              <Form.Text className="text-muted text-end mb-2">
                {t('spray.machines.calibrations.rowDataRequired')}
              </Form.Text>
            )}
            <div className="d-flex justify-content-end gap-2">
              {historyHasEntries && (
                <Button
                  variant="link"
                  size="sm"
                  type="button"
                  onClick={() => setShowHistory(true)}
                >
                  {t('spray.machines.calibrations.viewHistory')}
                </Button>
              )}
              <Button variant="secondary" onClick={onClose} type="button">
                {t('common.cancel')}
              </Button>
              <Button variant="primary" type="submit" disabled={saving || !allRowsPopulated}>
                {saving ? t('common.saving') : t('common.save')}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Form>
      {showHistory && calibration && (
        <CalibrationHistoryModal
          calibration={calibration}
          onClose={() => setShowHistory(false)}
        />
      )}
    </Modal>
  );
};

export default CalibrationModal;
